import { memo, useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  EVENT_EXPLORE,
  getActiveMenuId,
  getEventHubUrl,
  getViewedProfileUserName,
  hubRouteTarget,
  isProfileRoute,
  isViewingOtherProfile,
} from "./eventMenuConfig";
import EventV2SideMenu from "./EventV2SideMenu";
import EventV2ProfileCard from "./EventV2ProfileCard";
import useHttpHook from "../../../Hooks/HttpHook";
import { GetUserByName } from "../../../utils/ApiConstant";

const EventV2LeftRail = ({ activeId, onNavigate, menuOnly = false }) => {
  const { userDetails, masterToken } = useSelector((state) => state.user);
  const location = useLocation();
  const [, sendRequest] = useHttpHook();
  const [profileUser, setProfileUser] = useState(null);

  const viewedProfileUserName = getViewedProfileUserName(
    location.pathname,
    location.search
  );
  const viewingOtherProfile = isViewingOtherProfile(
    userDetails,
    viewedProfileUserName
  );
  const menuProfileUserName = viewingOtherProfile ? viewedProfileUserName : null;
  const resolvedActiveId =
    activeId || getActiveMenuId(location.pathname);

  const loadProfileUser = useCallback(async () => {
    if (!viewingOtherProfile || !viewedProfileUserName) {
      setProfileUser(null);
      return;
    }

    if (viewedProfileUserName === userDetails?.userName) {
      setProfileUser(userDetails);
      return;
    }

    try {
      const res = await sendRequest(`${GetUserByName}/${viewedProfileUserName}`, {
        method: "get",
      });
      if (res?.status === 1) {
        setProfileUser(res.data);
      } else {
        setProfileUser(null);
      }
    } catch (err) {
      console.log({ err });
      setProfileUser(null);
    }
  }, [sendRequest, userDetails, viewedProfileUserName, viewingOtherProfile]);

  useEffect(() => {
    loadProfileUser();
  }, [loadProfileUser]);

  const handlePlatformLink = (item, e) => {
    if (item.to) return;
    e.preventDefault();
    const link = item.href;
    if (!masterToken) {
      window.location.href = link;
      return;
    }
    if (item.platform === "merch") {
      if (menuProfileUserName) {
        const merchBase = (
          process.env.REACT_APP_MERCH_URL || link || ""
        ).replace(/\/$/, "");
        window.location.href = `${merchBase}/auth/login/${masterToken}/${menuProfileUserName}`;
      } else {
        window.location.href = link;
      }
    } else if (item.platform === "social") {
      if (menuProfileUserName) {
        window.location.href = `${link}/u/${menuProfileUserName}`;
      } else {
        window.location.href = `${link}/user/auth/login?authId=${masterToken}`;
      }
    } else if (item.platform === "class") {
      if (menuProfileUserName) {
        window.location.href = `${link}/auth/${masterToken}/?userName=${menuProfileUserName}`;
      } else {
        window.location.href = `${link}/auth/${masterToken}`;
      }
    } else if (item.platform === "tv") {
      if (menuProfileUserName) {
        window.location.href = `${link}/@${menuProfileUserName}`;
      } else {
        window.location.href = `${link}/index.php?link1=external_auth&authId=${encodeURIComponent(masterToken)}`;
      }
    } else if (item.platform === "music" && menuProfileUserName) {
      window.location.href = `${link}/${menuProfileUserName}`;
    } else {
      window.location.href = link;
    }
  };

  const closeIfMobile = () => {
    if (onNavigate) onNavigate();
  };

  const onProfilePage = isProfileRoute(location.pathname);

  const goHub = (path = "/", search = "") => {
    window.location.assign(getEventHubUrl(path, search));
  };

  const sideMenu = (
    <EventV2SideMenu
      activeId={resolvedActiveId}
      userName={menuProfileUserName}
      profileUser={profileUser}
      onNavigate={onNavigate}
    />
  );

  const explorePanel = (
    <div className="dash-panel event-explore-panel">
      <div className="event-explore-title">Explore</div>
      <div className="event-side-chips" aria-label="Explore Danzflo areas">
        {EVENT_EXPLORE.map((item) =>
          item.to ? (
            <Link
              key={item.id}
              className="event-side-chip"
              to={hubRouteTarget(
                item.to === "/" ? "/" : item.to,
                menuProfileUserName
              )}
              aria-label={item.label}
              onClick={(e) => {
                if (onProfilePage || menuProfileUserName) {
                  e.preventDefault();
                  const search = menuProfileUserName
                    ? `?userName=${encodeURIComponent(menuProfileUserName)}`
                    : "";
                  goHub(item.to === "/" ? "/" : item.to, search);
                  return;
                }
                closeIfMobile();
              }}
            >
              <i className={`bi ${item.icon}`} aria-hidden="true"></i>
              <span>{item.label}</span>
            </Link>
          ) : (
            <a
              key={item.id}
              className="event-side-chip"
              href={item.href}
              aria-label={item.label}
              onClick={(e) => {
                handlePlatformLink(item, e);
                closeIfMobile();
              }}
            >
              <i className={`bi ${item.icon}`} aria-hidden="true"></i>
              <span>{item.label}</span>
            </a>
          )
        )}
      </div>
    </div>
  );

  if (menuOnly) {
    return (
      <nav className="event-hub-menu-drawer" aria-label="Event menu">
        {sideMenu}
        {explorePanel}
      </nav>
    );
  }

  return (
    <aside
      className="event-left-rail event-split-scroll"
      aria-label="Event navigation"
      data-active={resolvedActiveId}
    >
      <EventV2ProfileCard
        onNavigate={onNavigate}
        viewedUserName={viewingOtherProfile ? viewedProfileUserName : null}
        profileUser={viewingOtherProfile ? profileUser : null}
      />

      <nav className="dash-panel event-nav-panel" aria-label="Event menu">
        <div className="dash-panel-head">
          <div>
            <div className="dash-panel-title">DanzFlo</div>
            <div className="dash-panel-sub">Event</div>
          </div>
        </div>
        {sideMenu}
        {userDetails?.userType === "organizer" && !menuProfileUserName && (
          <Link
            className="event-side-cta"
            to={hubRouteTarget("/myEvents/create/basic_info")}
            onClick={closeIfMobile}
          >
            <i className="bi bi-calendar-plus" aria-hidden="true"></i>
            <span>Create Event</span>
          </Link>
        )}
      </nav>

      {explorePanel}
    </aside>
  );
};

export default memo(EventV2LeftRail);
